import { useState } from 'react';
import { WindowTabs, type BriefWindow } from '@/components/common/WindowTabs';
import { BriefView } from '@/components/brief/BriefView';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHero, KpiStat } from '@/components/common/PageHero';
import { useBriefLatest } from '@/lib/queries';
import { ApiError } from '@/lib/api';

export default function BriefToday() {
  const [windowKey, setWindowKey] = useState<BriefWindow>('7d');
  const { data, isLoading, error } = useBriefLatest(windowKey);

  const d = parseBriefDate(data?.briefDate) ?? new Date();
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const day = d.getDate();
  const monthYear = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const windowLabel = windowKey === '24h' ? 'Last 24 hours' : windowKey === '7d' ? 'Last 7 days' : 'Last 30 days';
  const themeCount =
    data ? Object.values(data.themes).filter((t) => t.summary.trim().length > 0).length : 0;

  return (
    <div>
      <PageHero
        eyebrow={`Morning Brief · ${weekday}`}
        title={
          <>
            {day} <span className="text-muted font-normal">{monthYear}</span>
          </>
        }
        lede="Drilling-digital market awareness — curated, scored, and synthesized. Choose a window to shift between breaking signals and structural shifts."
        side={<WindowTabs value={windowKey} onChange={setWindowKey} />}
        stats={
          data && (
            <dl className="flex gap-8 md:gap-12 flex-wrap">
              <KpiStat label="Items scored" value={data.totalItems} tone="accent" />
              <KpiStat label="Themes covered" value={themeCount} tone="accent" />
              <KpiStat label="Window" value={windowLabel} />
            </dl>
          )
        }
      />

      {isLoading && <LoadingSkeleton lines={6} />}
      {renderError(error)}
      {data && <BriefView brief={data} heading={headingFor(windowKey)} />}
    </div>
  );
}

function renderError(error: unknown): React.ReactNode {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 404) {
    return (
      <section className="editorial-card">
        <span className="eyebrow">No brief yet</span>
        <p className="text-muted mt-2">
          No brief has been generated for this window. Trigger{' '}
          <code className="font-mono text-text">daily-brief</code> from Debug → Triggers.
        </p>
      </section>
    );
  }
  return <ErrorState message={(error as Error).message} />;
}

function headingFor(w: BriefWindow): string {
  if (w === '24h') return 'What moved in the last 24 hours';
  if (w === '7d') return 'This week in drilling-digital';
  return 'The shape of the last 30 days';
}

function parseBriefDate(s: string | undefined): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
